import * as ToastPrimitive from "@radix-ui/react-toast"
import { createContext, useCallback, useContext, useState } from "react"
import { cn } from "../../lib/cn"

type ToastVariant = "default" | "success" | "warning" | "error"

interface ToastItem {
  id: string
  title: string
  description?: string
  variant: ToastVariant
}

interface ToastCtx {
  show: (t: Omit<ToastItem, "id"> & { variant?: ToastVariant }) => void
}

const Ctx = createContext<ToastCtx | null>(null)

export function useToast(): ToastCtx {
  const v = useContext(Ctx)
  if (!v) throw new Error("useToast must be used inside <ToastProvider>")
  return v
}

const VARIANT_CLASS: Record<ToastVariant, string> = {
  default: "border-border-default",
  success: "border-success",
  warning: "border-warning",
  error: "border-danger",
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const show = useCallback<ToastCtx["show"]>(({ title, description, variant }) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    setToasts((prev) => [...prev, { id, title, description, variant: variant ?? "default" }])
  }, [])

  return (
    <Ctx.Provider value={{ show }}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}
        {toasts.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            className={cn(
              "rounded-lg border bg-surface p-4 shadow-lg",
              "data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full",
              VARIANT_CLASS[t.variant],
            )}
            onOpenChange={(open) => {
              if (!open) {
                setToasts((prev) => prev.filter((x) => x.id !== t.id))
              }
            }}
          >
            <ToastPrimitive.Title className="text-sm font-semibold text-text-primary">
              {t.title}
            </ToastPrimitive.Title>
            {t.description ? (
              <ToastPrimitive.Description className="mt-1 text-xs text-text-secondary">
                {t.description}
              </ToastPrimitive.Description>
            ) : null}
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-50 flex w-96 max-w-full flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </Ctx.Provider>
  )
}
